// Branded header — sticky at top, scrolls with the page.
// Logo links to story-stamp.com. Uses the real brand mark and wordmark.

export function StampHeader() {
  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 px-5 py-2.5 border-b border-border bg-parchment/95 backdrop-blur-sm">
      <a
        href="https://story-stamp.com"
        className="flex items-center gap-2.5 no-underline"
        aria-label="Story Stamp home"
      >
        <StampMark className="w-8 h-[41px] flex-shrink-0" />
        <Wordmark />
      </a>
    </header>
  );
}

// Standalone seal/stamp mark — used on the loading / empty / error states and
// the bare-domain landing page. Just the stamp graphic, sized by the caller.
// The SVG keeps its own aspect ratio (preserveAspectRatio defaults to "meet"),
// so a square className like `w-16 h-16` centers the mark without distorting it.
export function SealLogo({ className = "" }: { className?: string }) {
  return <StampMark className={className} />;
}

// The perforated vintage stamp mark — simplified from the v6 brand suite.
function StampMark({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 192 244"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Perforation dots — top */}
      <g fill="#EDEBE4">
        {[28,42,56,70,84,98,112,126,140,154,168].map(cx => (
          <circle key={`t${cx}`} cx={cx} cy="16" r="5.5"/>
        ))}
        {/* bottom */}
        {[28,42,56,70,84,98,112,126,140,154,168].map(cx => (
          <circle key={`b${cx}`} cx={cx} cy="228" r="5.5"/>
        ))}
        {/* left */}
        {[30,44,58,72,86,100,114,128,142,156,170,184,198,212].map(cy => (
          <circle key={`l${cy}`} cx="16" cy={cy} r="5.5"/>
        ))}
        {/* right */}
        {[30,44,58,72,86,100,114,128,142,156,170,184,198,212].map(cy => (
          <circle key={`r${cy}`} cx="176" cy={cy} r="5.5"/>
        ))}
      </g>
      {/* Stamp body */}
      <rect x="26" y="26" width="140" height="192" rx="2" fill="#2E6B3E"/>
      <rect x="30" y="30" width="132" height="184" rx="1" fill="#3D8C52"/>
      <rect x="35" y="35" width="122" height="174" rx="1" fill="none" stroke="#D4EDD9" strokeWidth="0.8" opacity="0.3"/>
      {/* Wordmark inside stamp */}
      <text x="96" y="57" textAnchor="middle" fontFamily="'Bodoni Moda',Georgia,serif" fontStyle="italic" fontSize="14" fill="#FFFFFF" opacity="0.92">Story</text>
      <text x="96" y="71" textAnchor="middle" fontFamily="'Gill Sans Nova','Gill Sans',sans-serif" fontWeight="600" fontSize="11" letterSpacing="2" fill="#D4EDD9">STAMP</text>
      <line x1="52" y1="80" x2="140" y2="80" stroke="#D4EDD9" strokeWidth="0.6" opacity="0.3"/>
      {/* Play circle */}
      <circle cx="96" cy="133" r="42" fill="#2E6B3E"/>
      <circle cx="96" cy="133" r="42" fill="none" stroke="#D4EDD9" strokeWidth="0.8" opacity="0.22"/>
      <polygon points="80,113 80,153 122,133" fill="#FFFFFF" opacity="0.95"/>
      {/* Tagline */}
      <text x="96" y="191" textAnchor="middle" fontFamily="'Gill Sans Nova','Gill Sans',sans-serif" fontSize="7.5" fontWeight="300" letterSpacing="3.5" fill="#D4EDD9" opacity="0.7">EVERY STAMP,</text>
      <text x="96" y="203" textAnchor="middle" fontFamily="'Gill Sans Nova','Gill Sans',sans-serif" fontSize="7.5" fontWeight="300" letterSpacing="3.5" fill="#D4EDD9" opacity="0.7">A STORY</text>
    </svg>
  );
}

// Two-part wordmark: "Story" italic serif + "Stamp" bold green
function Wordmark() {
  return (
    <div className="flex flex-col leading-none">
      <div className="flex items-baseline gap-[2px]">
        <span
          className="text-textPrimary text-[17px] leading-none"
          style={{ fontFamily: "'Bodoni Moda', Georgia, serif", fontStyle: "italic" }}
        >
          Story
        </span>
        <span
          className="text-primary text-[17px] font-semibold leading-none tracking-tight"
          style={{ fontFamily: "'Gill Sans Nova', 'Gill Sans', sans-serif" }}
        >
          Stamp
        </span>
      </div>
      <span
        className="text-textTertiary text-[9px] uppercase tracking-[0.2em] mt-0.5"
        style={{ fontFamily: "'Gill Sans Nova', 'Gill Sans', sans-serif", fontWeight: 300 }}
      >
        Every Stamp, a Story
      </span>
    </div>
  );
}
